import { Role } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { UserFromAPI } from 'api/user.service';

export interface UserRoles extends Pick<UserFromAPI, 'systemRoles' | 'roles'> {}

export interface ParsedSystemRoles {
    isSysAdmin: boolean;
    isOrgAdmin: boolean;
    isReadOnly: boolean;
    isMaintenance: boolean;
    isDiagnostics: boolean;
}

export function parseSystemRole(systemRoles: string[]): ParsedSystemRoles {
    const isSysAdmin = systemRoles?.some((r) => r === 'systemAdmin');
    const isOrgAdmin = systemRoles?.some((r) => r === 'orgAdmin');
    const isReadOnly = systemRoles?.some((r) => r === 'readonly');
    const isMaintenance = systemRoles?.some((r) => r === 'maintenance');
    const isDiagnostics = systemRoles?.some((r) => r === 'chargerDiagnostics');

    return {
        isSysAdmin,
        isOrgAdmin,
        isReadOnly,
        isMaintenance,
        isDiagnostics,
    };
}

export function getUserAccessDescription(user: UserRoles, allCustomRoles?: Role[]) {
    const systemRoles = parseSystemRole(user.systemRoles);
    const customRoleNames =
        user.roles
            ?.map((roleId) =>
                roleId ? allCustomRoles?.find((r) => r.roleId === roleId)?.name || `Custom role (${roleId})` : ''
            )
            .filter(Boolean) || [];

    const accessDescription = [
        systemRoles.isSysAdmin ? 'System Admin' : '',
        systemRoles.isOrgAdmin ? 'Organization Admin' : '',
        systemRoles.isMaintenance ? 'Service tools' : '',
        systemRoles.isReadOnly ? 'Viewer' : '',
        systemRoles.isDiagnostics ? 'Diagnostics' : '',
        ...customRoleNames,
    ].filter(Boolean);

    return accessDescription;
}

export function getUserAccessDescriptionString(user: UserRoles, allCustomRoles?: Role[]) {
    let accessDescription = getUserAccessDescription(user, allCustomRoles).join(', ');

    if (!accessDescription) {
        accessDescription = 'Default permissions';
    }

    return accessDescription;
}

/**
 * readonly && !default => false
 *
 * maintenance && chargerDiagnostics => false
 */
export function checkSystemRoles(input: string[]): boolean {
    if (!input.length) {
        // bail out early if empty array
        return true;
    }

    // Parse system roles
    const { isSysAdmin, isOrgAdmin, isReadOnly, isDiagnostics, isMaintenance } = parseSystemRole(input);

    // Cases
    if (isReadOnly && (isSysAdmin || isOrgAdmin || isDiagnostics || isMaintenance)) {
        return false;
    } else if (isMaintenance && isDiagnostics) {
        return false;
    }

    return true;
}
